import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Group, Loader, MultiSelect, Text, Textarea, TextInput, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { DateInput } from '@mantine/dates';
import { useNavigate } from 'react-router-dom';
import HttpClient from '../helpers/HttpClient';
import { Message, MessageRecipientOptions } from '../types/message.type';

interface MessageFormValues {
  id: number;
  subject: string;
  body: string;
  validFrom: Date | null;
  validTo: Date | null;
  recipientPersonIds: string[];
  recipientTagIds: string[];
}

const MessageEdit: React.FC<{ id: number | undefined }> = ({ id }) => {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isUpdate, setIsUpdate] = useState(false);
  const [options, setOptions] = useState<MessageRecipientOptions>({ persons: [], tags: [] });
  const [reach, setReach] = useState<{ recipients: number; parents: number } | null>(null);
  const navigate = useNavigate();

  const form = useForm<MessageFormValues>({
    initialValues: {
      id: 0,
      subject: '',
      body: '',
      validFrom: null,
      validTo: null,
      recipientPersonIds: [],
      recipientTagIds: [],
    },
    validate: {
      subject: (v) => (v?.trim() ? null : 'Betreff ist erforderlich'),
      body: (v) => (v?.trim() ? null : 'Nachrichtentext ist erforderlich'),
      validTo: (v, values) => {
        if (v && values.validFrom && v < values.validFrom) {
          return '"Gültig bis" muss nach "Gültig ab" liegen';
        }
        return null;
      },
      recipientPersonIds: (_v, values) => {
        if (!values.recipientPersonIds.length && !values.recipientTagIds.length) {
          return 'Mindestens ein Empfänger (Person oder Tag) ist erforderlich';
        }
        return null;
      },
      recipientTagIds: (_v, values) => {
        if (!values.recipientPersonIds.length && !values.recipientTagIds.length) {
          return 'Mindestens ein Empfänger (Person oder Tag) ist erforderlich';
        }
        return null;
      },
    },
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const recipientOptions = await new HttpClient<MessageRecipientOptions>('/message/recipients').getSingle();
        if (recipientOptions) setOptions(recipientOptions);

        if (id) {
          const result: any = await new HttpClient<Message>('/message').getById(id);
          const msg = result.data as Message;
          form.setValues({
            id: msg.id,
            subject: msg.subject,
            body: msg.body,
            validFrom: msg.validFrom ? new Date(msg.validFrom) : null,
            validTo: msg.validTo ? new Date(msg.validTo) : null,
            recipientPersonIds: (msg.recipientPersons ?? []).map((p) => String(p.id)),
            recipientTagIds: (msg.recipientTags ?? []).map((t) => String(t.id)),
          });
          setIsUpdate(true);
        }
      } catch {
        setLoadError('Daten konnten nicht geladen werden. Bitte Seite neu laden.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Live count of who the chosen recipients reach (parents included).
  const { recipientPersonIds, recipientTagIds } = form.values;
  const personKey = recipientPersonIds.join(',');
  const tagKey = recipientTagIds.join(',');
  useEffect(() => {
    if (!personKey && !tagKey) { setReach(null); return; }
    let cancelled = false;
    new HttpClient<{ recipients: number; parents: number }>('/message/reach')
      .getSingle({ personIds: personKey, tagIds: tagKey })
      .then((r) => { if (!cancelled) setReach(r); })
      .catch(() => { if (!cancelled) setReach(null); });
    return () => { cancelled = true; };
  }, [personKey, tagKey]);

  const onSubmit = async (values: MessageFormValues) => {
    setSubmitError(null);
    try {
      const payload = {
        id: values.id,
        subject: values.subject,
        body: values.body,
        validFrom: values.validFrom ?? undefined,
        validTo: values.validTo ?? undefined,
        recipientPersonIds: values.recipientPersonIds.map(Number),
        recipientTagIds: values.recipientTagIds.map(Number),
      };
      const response: any = await new HttpClient<any>('/message').post(payload as any);
      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Speichern fehlgeschlagen');
      }
      navigate('/messages');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Nachricht konnte nicht gespeichert werden');
    }
  };

  const onDelete = async () => {
    if (!window.confirm('Nachricht wirklich löschen?')) return;
    setSubmitError(null);
    try {
      if (!id) throw new Error('Kein ID zum Löschen');
      const response = await new HttpClient<Message>('/message').delete(id);
      if (response.status !== 200) throw new Error('Löschen fehlgeschlagen');
      navigate('/messages');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Löschen fehlgeschlagen');
    }
  };

  if (loading) return <Loader size="md" mt="xl" />;

  if (loadError) {
    return (
      <Box p="md">
        <Alert color="red" title="Fehler">{loadError}</Alert>
        <Button mt="md" variant="default" onClick={() => navigate('/messages')}>Zurück</Button>
      </Box>
    );
  }

  const personOptions = options.persons.map((p) => ({
    value: String(p.id),
    label: `${p.lastName}, ${p.firstName}`,
  }));

  const tagOptions = options.tags.map((t) => ({
    value: String(t.id),
    label: t.name,
  }));

  return (
    <Box mx="auto" maw={800} p="md">
      <Title order={2} mb="md">{isUpdate ? 'Nachricht bearbeiten' : 'Neue Nachricht'}</Title>
      {submitError && <Alert color="red" title="Fehler" mb="md" withCloseButton onClose={() => setSubmitError(null)}>{submitError}</Alert>}
      <form onSubmit={form.onSubmit(onSubmit)}>
        <TextInput label="Betreff" mb="sm" {...form.getInputProps('subject')} />
        <Textarea
          label="Nachricht (HTML)"
          minRows={8}
          autosize
          mb="sm"
          {...form.getInputProps('body')}
        />
        <Group grow mb="sm">
          <DateInput
            label="Gültig ab"
            clearable
            valueFormat="DD.MM.YYYY"
            {...form.getInputProps('validFrom')}
          />
          <DateInput
            label="Gültig bis"
            clearable
            valueFormat="DD.MM.YYYY"
            {...form.getInputProps('validTo')}
          />
        </Group>
        <MultiSelect
          label="Empfänger (Personen)"
          data={personOptions}
          searchable
          clearable
          mb="sm"
          {...form.getInputProps('recipientPersonIds')}
        />
        <MultiSelect
          label="Empfänger (Tags)"
          data={tagOptions}
          searchable
          clearable
          mb="xs"
          {...form.getInputProps('recipientTagIds')}
        />
        {reach && (
          <Text size="sm" c="dimmed" mb="md">
            Erreicht {reach.recipients} {reach.recipients === 1 ? 'Person' : 'Personen'}
            {reach.parents > 0 && ` und ${reach.parents} ${reach.parents === 1 ? 'Elternteil' : 'Eltern'}`} auf dem Dashboard.
          </Text>
        )}
        <Group mt="md">
          <Button type="submit">Speichern</Button>
          <Button variant="default" onClick={() => navigate('/messages')}>Abbrechen</Button>
          {isUpdate && (
            <Button color="red" onClick={onDelete}>Löschen</Button>
          )}
        </Group>
      </form>
    </Box>
  );
};

export default MessageEdit;
